/**
 * Hariom ERP - Finance Module
 * Invoices entered by the finance team drive the KPIs and the monthly
 * revenue-vs-expenses chart. All records persist.
 */
import React, { useState, useMemo } from 'react'
import { Plus, Edit3, Trash2 } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import toast from 'react-hot-toast'
import { useInvoiceStore, type Invoice } from '../../store/financeStore'
import FormModal, { type FormField } from '../../components/ui/FormModal'

const STATUS_CLS: Record<string, string> = {
  paid: 'badge-success', overdue: 'badge-danger', pending: 'badge-warning', partial: 'badge-info',
}

const INVOICE_FIELDS: FormField[] = [
  { name: 'invoiceNo', label: 'Invoice No', required: true, placeholder: 'e.g. INV-2026-104' },
  { name: 'type',      label: 'Type', type: 'select', options: ['sales', 'purchase'], required: true },
  { name: 'party',     label: 'Party', required: true, placeholder: 'e.g. SP SINGLA', full: true },
  { name: 'amount',    label: 'Amount (₹, pre-GST)', type: 'number', placeholder: '0' },
  { name: 'gst',       label: 'GST (₹)', type: 'number', placeholder: '0' },
  { name: 'paid',      label: 'Paid (₹)', type: 'number', placeholder: '0' },
  { name: 'status',    label: 'Status', type: 'select', options: ['paid', 'overdue', 'pending', 'partial'] },
  { name: 'date',      label: 'Invoice Date', type: 'date' },
  { name: 'due',       label: 'Due Date', type: 'date' },
]

const num = (v: string) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0 }

const Finance: React.FC = () => {
  const { items: invoices, add, update, remove } = useInvoiceStore()
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Invoice | null>(null)

  const totalRev = invoices.filter(i => i.type === 'sales').reduce((s, i) => s + i.total, 0)
  const totalExp = invoices.filter(i => i.type === 'purchase').reduce((s, i) => s + i.total, 0)
  const pendingRec = invoices.filter(i => i.type === 'sales' && i.status !== 'paid').reduce((s, i) => s + (i.total - i.paid), 0)
  const pendingPay = invoices.filter(i => i.type === 'purchase' && i.status !== 'paid').reduce((s, i) => s + (i.total - i.paid), 0)

  // Monthly revenue vs expenses, derived from invoice dates.
  const monthlyData = useMemo(() => {
    const map = new Map<string, { month: string; revenue: number; expenses: number; order: number }>()
    invoices.forEach(i => {
      if (!i.date) return
      const d = new Date(i.date)
      if (isNaN(d.getTime())) return
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      const e = map.get(key) || { month: d.toLocaleString('en', { month: 'short' }), revenue: 0, expenses: 0, order: d.getFullYear() * 12 + d.getMonth() }
      if (i.type === 'sales') e.revenue += i.total; else e.expenses += i.total
      map.set(key, e)
    })
    return [...map.values()].sort((a, b) => a.order - b.order)
  }, [invoices])

  const handleSave = (v: Record<string, string>) => {
    const invoiceNo = v.invoiceNo.trim()
    if (invoices.some(i => i.invoiceNo.toLowerCase() === invoiceNo.toLowerCase() && i.id !== editing?.id)) {
      toast.error(`Invoice "${invoiceNo}" already exists`); return
    }
    const amount = num(v.amount), gst = num(v.gst)
    const rec: Invoice = {
      id: editing ? editing.id : `inv-${Date.now()}`,
      invoiceNo, type: v.type || 'sales', party: v.party.trim(),
      amount, gst, total: amount + gst, status: v.status || 'pending',
      date: v.date, due: v.due, paid: num(v.paid),
    }
    if (editing) { update(editing.id, rec); toast.success(`Updated ${rec.invoiceNo}`) }
    else { add(rec); toast.success(`Invoice created`) }
    setModalOpen(false); setEditing(null)
  }

  const handleDelete = (i: Invoice) => {
    if (window.confirm(`Delete ${i.invoiceNo}?`)) { remove(i.id); toast.success('Deleted') }
  }

  const toRecord = (i: Invoice): Record<string, string> => ({
    invoiceNo: i.invoiceNo, type: i.type, party: i.party, amount: String(i.amount),
    gst: String(i.gst), paid: String(i.paid), status: i.status, date: i.date, due: i.due,
  })

  return (
    <div className="space-y-5">
      <div className="page-header">
        <div>
          <h1 className="page-title">Finance</h1>
          <p className="page-subtitle">Invoices, cash flow, P&amp;L, receivables &amp; payables</p>
        </div>
        <button onClick={() => { setEditing(null); setModalOpen(true) }} className="btn-primary text-sm flex items-center gap-2"><Plus size={14}/> Create Invoice</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card py-3 border-l-4 border-l-green-500"><p className="text-xs text-steel-500">Revenue (Sales)</p><p className="text-xl font-bold text-green-700">₹{(totalRev / 10000000).toFixed(2)} Cr</p></div>
        <div className="card py-3 border-l-4 border-l-red-500"><p className="text-xs text-steel-500">Expenses (Purchases)</p><p className="text-xl font-bold text-red-600">₹{(totalExp / 10000000).toFixed(2)} Cr</p></div>
        <div className="card py-3 border-l-4 border-l-blue-500"><p className="text-xs text-steel-500">Pending Receivables</p><p className="text-xl font-bold text-blue-700">₹{(pendingRec / 100000).toFixed(1)} L</p></div>
        <div className="card py-3 border-l-4 border-l-yellow-500"><p className="text-xs text-steel-500">Pending Payables</p><p className="text-xl font-bold text-yellow-600">₹{(pendingPay / 100000).toFixed(1)} L</p></div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="font-semibold text-steel-800 mb-4">Revenue vs Expenses (Monthly)</h3>
          {monthlyData.length === 0 ? (
            <div className="h-[220px] flex items-center justify-center text-steel-400 text-sm">Create invoices to see the chart</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                <XAxis dataKey="month" tick={{ fontSize: 11 }}/>
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₹${(v / 100000).toFixed(0)}L`}/>
                <Tooltip formatter={(v: number) => [`₹${(v / 100000).toFixed(1)}L`]}/>
                <Legend/>
                <Bar dataKey="revenue"  name="Revenue"  fill="#2E7D32" radius={[3, 3, 0, 0]}/>
                <Bar dataKey="expenses" name="Expenses" fill="#C41E3A" radius={[3, 3, 0, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="card p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-steel-100"><h3 className="font-semibold text-steel-800">Invoices</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="bg-steel-50">
                <th className="table-header">Invoice</th><th className="table-header">Party</th>
                <th className="table-header text-right">Total</th><th className="table-header">Status</th>
                <th className="table-header">Due</th><th className="table-header text-center">Actions</th>
              </tr></thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr><td colSpan={6} className="text-center text-steel-400 text-sm py-10">No invoices yet. Click <span className="font-semibold text-steel-600">“Create Invoice”</span>.</td></tr>
                ) : invoices.map(i => (
                  <tr key={i.id} className="table-row-hover">
                    <td className="table-cell font-mono text-xs"><span className={i.type === 'sales' ? 'text-green-600' : 'text-red-600'}>{i.invoiceNo}</span></td>
                    <td className="table-cell text-sm">{i.party}</td>
                    <td className="table-cell text-right font-bold text-sm">₹{i.total.toLocaleString()}</td>
                    <td className="table-cell"><span className={STATUS_CLS[i.status] || 'badge-info'}>{i.status}</span></td>
                    <td className="table-cell text-xs text-steel-500">{i.due || '—'}</td>
                    <td className="table-cell text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => { setEditing(i); setModalOpen(true) }} className="p-1.5 text-steel-500 hover:text-primary-600 hover:bg-primary-50 rounded" title="Edit"><Edit3 size={14}/></button>
                        <button onClick={() => handleDelete(i)} className="p-1.5 text-steel-500 hover:text-red-600 hover:bg-red-50 rounded" title="Delete"><Trash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {modalOpen && (
        <FormModal
          title={editing ? 'Edit Invoice' : 'Create Invoice'}
          submitLabel={editing ? 'Save Changes' : 'Create Invoice'}
          fields={INVOICE_FIELDS}
          initial={editing ? toRecord(editing) : {}}
          onClose={() => { setModalOpen(false); setEditing(null) }}
          onSave={handleSave}
        />
      )}
    </div>
  )
}

export default Finance
